import { action, makeObservable, observable } from 'mobx';

/**
 * Manager for guided tour configuration and state.
 *
 * Stores role-specific configuration for third-party guided tour providers
 * (districtAdmin, schoolAdmin, teacher, student, generic).
 *
 * For detailed implementation examples, configuration options, and how to add
 * new providers, see GuidedTourService.js.
 */
export class GuidedTourManager {
  /**
   * Configuration object for guided tours.
   *
   * Contains role-specific configurations (districtAdmin, schoolAdmin, teacher, student, generic).
   * Each role config contains its own guidedTourProvider and provider-specific settings.
   *
   * Configuration should be an object with role-specific settings:
   * ```js
   * {
   *   districtAdmin: {
   *     autoShowTourOnFirstVisit: true,
   *     guidedTourId: 'visited-guided-tour-district-admin',
   *     guidedTourProvider: 'navattic',
   *     guidedTourUrl: 'https://capture.navattic.com/abc123',
   *     navatticConfig: { title: 'District Admin Tour' }
   *   },
   *   schoolAdmin: { ... },
   *   teacher: { ... },
   *   student: { ... },
   *   generic: { ... }
   * }
   * ```
   *
   * @type {Object}
   */
  @observable guidedTourConfig = {};

  constructor() {
    makeObservable(this);
  }

  /**
   * Set the guided tour configuration.
   *
   * Configuration should be an object with role-specific settings:
   * ```js
   * {
   *   districtAdmin: {
   *     autoShowTourOnFirstVisit: true,
   *     guidedTourId: 'visited-guided-tour-district-admin',
   *     guidedTourProvider: 'navattic',
   *     guidedTourUrl: 'https://capture.navattic.com/abc123',
   *     navatticConfig: { title: 'District Admin Tour' }
   *   },
   *   schoolAdmin: { ... },
   *   teacher: { ... },
   *   student: { ... },
   *   generic: { ... }
   * }
   * ```
   *
   * @param {Object} config - Provider-specific configuration object with role-specific settings
   */
  @action setGuidedTourConfig = (config) => {
    this.guidedTourConfig = config;
  }

  @action clearAll = () => {
    /* placeholder */
  }
}

export default new GuidedTourManager();
